import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BorgStyleTextField from "./BorgStyleTextField";
import BorgStyleButton from "./BorgStyleButton";
import MainPageTemplateColumnLayout from '../Pages/Templates/MainPageTemplateColumnLayout';
import borgLogo from "../assets/img/Borg-logo.png";
import kepleraLogo from "../assets/img/Keplera-logo.png";
import { resetPassword } from "../Services/BorgAPIClient";

const isKepleraVersion = process.env.REACT_APP_PRODUCT_NAME === "keplera";
const productLogo = isKepleraVersion ? kepleraLogo : borgLogo;

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    const tokenParam = params.get("token");

    if (emailParam && tokenParam) {
      setEmail(decodeURIComponent(emailParam));
      setToken(decodeURIComponent(tokenParam));
    } else {
      setError("Invalid reset link.");
    }
  }, [location.search]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 10) {
      return "Password must be at least 10 characters.";
    }

    const regex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&_])[A-Za-z\d@$!%*#?&_]{10,255}$/;
    if (!regex.test(password)) {
      return "Must contain at least one letter, one number, and one special character: @$!%*#?&_";
    }

    return null;
  };

  const handleReset = async () => {
    if (isLoading) return; // Prevent multiple submissions
    
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    if (!newPassword || !confirmPassword) {
      setError("Please fill out both password fields.");
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      await resetPassword({
        email,
        token,
        newPassword
      });

      setSuccessMessage("Password successfully reset. You can now log in.");
      setTimeout(() => navigate("/"), 2000);
    } catch (e: any) {
      setError(e?.message || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div className="borg-logo" style={{ marginTop: '6vh' }}>
        <img alt="logo" src={productLogo} />
      </div>

      <MainPageTemplateColumnLayout title="Reset Your Password">
        <div className="box-column">
          <div className="box-column">
            <BorgStyleTextField
              id="textbox-new-password"
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
            />

            <BorgStyleTextField
              id="textbox-confirm-password"
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && !isLoading) handleReset();
              }}
            />

            {error && <div className="error">{error}</div>}
            {successMessage && <div style={{ color: "green" }}>{successMessage}</div>}
            {isLoading && (
              <div style={{ color: "#fcbf49", textAlign: "center", margin: "10px 0" }}>
                Processing your request...
              </div>
            )}
          </div>

          <div className="footer">
            <BorgStyleButton 
              id="button-submit-reset" 
              onClick={handleReset}
              disabled={isLoading}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </BorgStyleButton>
          </div>
        </div>
      </MainPageTemplateColumnLayout>
    </div>
  );
};

export default ResetPassword;
